/**
 * Restaurant subscription success page.
 *
 * Stripe redirects the merchant here with ?session_id=cs_xxx after a
 * successful checkout. We:
 *   1. Call /api/billing/finalize-session/{id} which acts as a webhook
 *      fallback (Stripe webhooks are async — they may arrive seconds later).
 *   2. Display a clear celebratory confirmation with the plan, trial date,
 *      Stripe customer + subscription IDs, and a single primary CTA to
 *      advance to the next onboarding step.
 *   3. The backend handler is idempotent + sends welcome + admin emails.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2,
  Sparkles,
  Crown,
  ArrowRight,
  Mail,
  Loader2,
  Copy,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from '../components/OnboardingShell';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { token, fetchMe } = useAuth();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState('loading'); // loading | ready | error
  const [data, setData] = useState(null);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    if (!sessionId) {
      setState('error');
      return;
    }
    let cancelled = false;
    const finalize = async () => {
      // Retry a few times in case the Stripe API still shows status=open
      for (let attempt = 0; attempt < 4 && !cancelled; attempt += 1) {
        try {
          const r = await axios.get(`${API}/api/billing/finalize-session/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const status = r.data?.status;
          if (status === 'trialing' || status === 'active') {
            if (!cancelled) {
              setData(r.data);
              setState('ready');
              await fetchMe?.();
            }
            return;
          }
        } catch (err) {
          if (attempt === 3 && !cancelled) {
            setState('error');
            return;
          }
        }
        await new Promise((res) => setTimeout(res, 1500));
      }
      if (!cancelled) setState('error');
    };
    finalize();
    return () => { cancelled = true; };
  }, [sessionId, token, fetchMe, navigate]);

  const advance = async () => {
    setAdvancing(true);
    try {
      await axios.post(
        `${API}/api/onboarding/advance`,
        { step: 'setup' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchMe?.();
      navigate('/onboarding/setup');
    } catch {
      toast.error('Could not continue. Try again.');
    } finally { setAdvancing(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch {}
  };

  // ─────────────────────────────────────────── states
  if (state === 'loading') {
    return (
      <OnboardingShell currentStep="subscribe" testid="subscription-success-loading">
        <div className="max-w-xl text-center mx-auto" data-testid="sub-success-loading">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-yellow-500/[0.08] border border-yellow-500/30 items-center justify-center mb-5">
            <Loader2 className="w-7 h-7 text-yellow-300 animate-spin" />
          </div>
          <h1 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-white">
            Finalizing your subscription…
          </h1>
          <p className="mt-3 text-[14px] text-white/55">
            Stripe is confirming your payment with us. This usually takes a few seconds.
          </p>
        </div>
      </OnboardingShell>
    );
  }

  if (state === 'error') {
    return (
      <OnboardingShell currentStep="subscribe" testid="subscription-success-error">
        <div className="max-w-xl text-center mx-auto" data-testid="sub-success-error">
          <h1 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-white">
            We couldn't confirm your subscription yet
          </h1>
          <p className="mt-3 text-[14px] text-white/65">
            Your payment may still be processing on Stripe. Refresh in a moment, or visit your dashboard — the subscription will appear there once it's confirmed.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-2.5 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              data-testid="sub-success-retry"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl border border-white/12 hover:border-yellow-500/40 text-white/85 text-[13px] font-bold"
            >
              Refresh
            </button>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black text-[13px] font-extrabold"
            >
              Go to dashboard <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </OnboardingShell>
    );
  }

  // ─────────────────────────────────────────── success
  const trialEndsHuman = formatDate(data?.trial_ends_at);
  return (
    <OnboardingShell currentStep="subscribe" testid="subscription-success-page">
      <div className="max-w-3xl mx-auto" data-testid="sub-success-ready">
        {/* Hero */}
        <div className="text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/35 items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-300" strokeWidth={1.7} />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] mb-4">
            <Crown className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
              Subscription active · Trial started
            </span>
          </div>
          <h1
            data-testid="sub-success-headline"
            className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white"
          >
            You're in.{' '}
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
              Welcome to NoFee.
            </span>
          </h1>
          <p className="mt-4 text-[14.5px] sm:text-[15px] text-white/65 leading-relaxed max-w-xl mx-auto">
            Your <strong className="text-white">{data?.plan || 'NoFee Ordering'}</strong> plan is now active. You're on a 45-day free trial — no charges until <strong className="text-white">{trialEndsHuman}</strong>.
            We've also sent a confirmation email with everything below.
          </p>
        </div>

        {/* Receipt card */}
        <div
          data-testid="sub-success-receipt"
          className="mt-8 p-5 sm:p-6 rounded-3xl border border-yellow-500/35 bg-gradient-to-br from-yellow-500/[0.06] via-zinc-950 to-black"
        >
          <h2 className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Your subscription
          </h2>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
            <div>
              <dt className="text-white/50">Plan</dt>
              <dd className="mt-0.5 text-white font-semibold" data-testid="sub-success-plan">{data?.plan || 'NoFee Ordering'}</dd>
            </div>
            <div>
              <dt className="text-white/50">Price after trial</dt>
              <dd className="mt-0.5 text-white font-semibold" data-testid="sub-success-price">{data?.price_label || '$299/month'}</dd>
            </div>
            <div>
              <dt className="text-white/50">Trial ends</dt>
              <dd className="mt-0.5 text-white font-semibold" data-testid="sub-success-trial">{trialEndsHuman}</dd>
            </div>
            <div>
              <dt className="text-white/50">First billing date</dt>
              <dd className="mt-0.5 text-white font-semibold" data-testid="sub-success-billing">{trialEndsHuman}</dd>
            </div>
            <div>
              <dt className="text-white/50">Stripe customer</dt>
              <dd className="mt-0.5 text-white/85 font-mono text-[12px] flex items-center gap-1.5 break-all" data-testid="sub-success-customer">
                {data?.stripe_customer_id || '—'}
                {data?.stripe_customer_id && (
                  <button
                    type="button"
                    onClick={() => copy(data.stripe_customer_id)}
                    className="text-white/40 hover:text-yellow-300 shrink-0"
                    aria-label="Copy customer ID"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-white/50">Subscription</dt>
              <dd className="mt-0.5 text-white/85 font-mono text-[12px] flex items-center gap-1.5 break-all" data-testid="sub-success-subscription">
                {data?.stripe_subscription_id || '—'}
                {data?.stripe_subscription_id && (
                  <button
                    type="button"
                    onClick={() => copy(data.stripe_subscription_id)}
                    className="text-white/40 hover:text-yellow-300 shrink-0"
                    aria-label="Copy subscription ID"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* What's next */}
        <div className="mt-7 p-5 sm:p-6 rounded-3xl border border-white/8 bg-white/[0.02]" data-testid="sub-success-next">
          <h2 className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3">What's next</h2>
          <ol className="space-y-2.5 text-[13.5px] text-white/80">
            <li className="flex gap-2.5"><span className="w-5 h-5 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 text-[11px] font-bold flex items-center justify-center shrink-0">1</span>Finish your menu setup &amp; restaurant profile</li>
            <li className="flex gap-2.5"><span className="w-5 h-5 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 text-[11px] font-bold flex items-center justify-center shrink-0">2</span>Generate your QR code &amp; go live</li>
            <li className="flex gap-2.5"><span className="w-5 h-5 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 text-[11px] font-bold flex items-center justify-center shrink-0">3</span>Share with your customers — 0% commission, direct to your bank</li>
          </ol>

          <button
            type="button"
            onClick={advance}
            disabled={advancing}
            data-testid="sub-success-continue"
            className="mt-6 w-full sm:w-auto group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight disabled:opacity-60 transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
          >
            {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {advancing ? 'Loading…' : 'Continue to setup'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>

        <p className="mt-6 text-center text-[12.5px] text-white/40">
          <Mail className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
          Need help? Email <a href="mailto:support@nofee.restaurant" className="text-yellow-300 hover:text-yellow-200">support@nofee.restaurant</a>
        </p>
      </div>
    </OnboardingShell>
  );
};

export default SubscriptionSuccessPage;
